/**
 * Сводка варианта посевного плана (этап 2(г) планирования-2027).
 *
 * Макет (утверждён founder 18.08):
 *   `docs/superpowers/prototypes/annual-planning/v2-14-variant-summary.html`
 *   (ветка `docs/wireframes-season-planning-v2`, скриншот рядом).
 *
 * Что экран обещает пользователю:
 *   • что вариант даёт в тоннах и деньгах: валовка, выручка, затраты, маржа —
 *     по каждой культуре и в целом;
 *   • маржа с гектара считается на ВСЮ пашню, включая пар и нераспределённые
 *     поля: иначе показатель льстит хозяйству;
 *   • непокрытая площадь не превращается в ноль и не размазывается по
 *     остальным культурам — она отдельной строкой «не в расчёте»;
 *   • «устарело» — не значок, а таблица «что именно изменилось», и пересчёт
 *     по кнопке, а не молча.
 *
 * Чего здесь СОЗНАТЕЛЬНО нет (и почему):
 *   • «оставить расчёт от 21.07» и история расчётов — требуют хранения СНИМКА
 *     расчёта; сейчас числа всегда на текущих бюджетных ценах, а «устарело»
 *     показывает, что изменилось с базы последнего расчёта. Снимок — следующий
 *     PR (миграция);
 *   • «на сколько подвинет итог» в рублях — оттуда же: чтобы показать «было →
 *     станет», нужно уметь считать по СТАРЫМ ценам;
 *   • долей классов качества — решение founder 29.07: в первом сезоне одна
 *     цена на культуру. Если в бюджете заведено несколько классов, строка
 *     честно говорит, что взята средняя.
 */
import { formatMoneyNumber } from '@/lib/planning/money';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TileGrid } from '@/components/ui/tile-grid';
import { describeApiError } from '@/lib/apiError';
import { useCan } from '@/lib/auth/usePermissions';
import {
  VERDICT_BADGE,
  VERDICT_TITLES,
  type TargetMetric,
} from '@/lib/planning/budgetTargets';
import { useSeasonByYear } from '@/lib/planning/useSeasonByYear';
import {
  compareVariants,
  costSplit,
  formatDate,
  formatDeltaByUnit,
  formatHa,
  formatMln,
  formatPct,
  formatRubPerHa,
  formatTons,
  formatValueByUnit,
  formatWipMonth,
  groupRowsByCrop,
  rowTitle,
  useRecalculateVariant,
  useVariantSummary,
  useVariantWipReport,
  type CalcChange,
  type SummaryBasis,
  type VariantSummary,
} from '@/lib/planning/planVariant';
import { usePlanVariants } from '@/lib/planning/techCards';
import { FarmScopeNotice } from '@/components/scope/FarmScopeNotice';
import { usePlanningFarmScope } from '@/components/scope/usePlanningFarmScope';
import { SeasonNotStartedCard, SeasonYearOutOfWindowCard } from '../SeasonBudgetStates';
import { VariantTabs } from './VariantTabs';
import { MonthlyReportBlock } from '../tech-cards/MonthlyReportBlock';
import { ProcurementBlock } from '../tech-cards/ProcurementBlock';

export function PlanVariantSummaryPage() {
  const params = useParams({ strict: false }) as { year?: string };
  const year = Number(params.year);
  const search = useSearch({ strict: false }) as { farm?: string; variant?: string };
  const navigate = useNavigate();
  const season = useSeasonByYear(year);
  const canRead = useCan('tech_cards', 'r');
  const canWrite = useCan('tech_cards', 'u');

  // Хозяйство — из переключателя слева (AK-866 В-3: область — из
  // переключателя); `?farm=` — только присланная ссылка (AK-897).
  const farmScope = usePlanningFarmScope({
    searchFarm: search.farm,
    onScopeChanged: () =>
      void navigate({
        to: '/planning/seasons/$year/plan-variant/summary',
        params: { year: String(year) },
        search: { farm: undefined, variant: undefined },
        replace: true,
      }),
    onDeepLinkApplied: () =>
      void navigate({
        to: '/planning/seasons/$year/plan-variant/summary',
        params: { year: String(year) },
        search: { ...search, farm: undefined },
        replace: true,
      }),
  });
  const { farmId } = farmScope;
  const variants = usePlanVariants(canRead ? farmId : null, season.seasonId);
  const variantId =
    search.variant ?? variants.data?.find((v) => v.is_selected)?.id ?? variants.data?.[0]?.id ?? null;

  // Подача экономики (ADR-070): «по урожаю» — все затраты урожая (default),
  // «по календарю» — деньги календарного года сезона. Сравнение вариантов
  // идёт в ТОЙ ЖЕ подаче — сверять урожайную с календарной значит врать.
  const [basis, setBasis] = useState<SummaryBasis>('harvest');
  // Этот экран показывает блок НЗП с исходящим — единственный, кто просит
  // include_wip (дорогой расчёт плана X+1 не навешивается на все /summary).
  const summary = useVariantSummary(canRead ? variantId : null, basis, true);
  const [compareId, setCompareId] = useState<string | null>(null);
  const compare = useVariantSummary(compareId, basis);
  const recalculate = useRecalculateVariant();

  // Явный путь роута вместо относительного `.` — см. комментарий в экране
  // назначения: относительная навигация резолвится от `from`, которого нет.
  const setSearch = (patch: Record<string, string | undefined>) => {
    void navigate({
      to: '/planning/seasons/$year/plan-variant/summary',
      params: { year: String(year) },
      search: { farm: search.farm, variant: variantId ?? undefined, ...patch },
    });
  };

  if (!season.validYear || season.missing) {
    return (
      <Shell year={params.year}>
        <SeasonYearOutOfWindowCard year={year} />
      </Shell>
    );
  }
  if (season.isLoading) {
    return (
      <Shell year={params.year}>
        <Skeleton className="h-64 w-full" />
      </Shell>
    );
  }
  if (season.notOpened) {
    return (
      <Shell year={params.year}>
        <SeasonNotStartedCard year={year} onOpened={() => undefined} />
      </Shell>
    );
  }
  if (!canRead) {
    return (
      <Shell year={params.year}>
        <Alert>
          <AlertDescription>У вас нет доступа к экономике вариантов плана.</AlertDescription>
        </Alert>
      </Shell>
    );
  }

  const data = summary.data;
  const otherVariants = (variants.data ?? []).filter((v) => v.id !== variantId);

  return (
    <Shell year={params.year} title={data ? `Сводка варианта «${data.scenario.name}»` : undefined}>
      <FarmScopeNotice scope={farmScope} />
      {farmId && (variants.data ?? []).length > 0 && (
        <VariantTabs
          variants={variants.data ?? []}
          currentId={variantId}
          onSelect={(id) => {
            setCompareId(null);
            setSearch({ variant: id });
          }}
        />
      )}

      {variantId && summary.isLoading && <Skeleton className="h-96 w-full" />}
      {variantId && summary.isError && (
        <Alert variant="destructive">
          <AlertDescription>Не удалось посчитать вариант. Обновите страницу.</AlertDescription>
        </Alert>
      )}
      {/* Ошибка ≠ пустота: «нет вариантов — считать нечего» при упавшем
          чтении списка — враньё. */}
      {variants.isError && farmId && (
        <Alert variant="destructive">
          <AlertDescription>{describeApiError(variants.error)}</AlertDescription>
        </Alert>
      )}
      {!variantId && !variants.isLoading && !variants.isError && farmId && (
        <Alert>
          <AlertDescription>
            У этого хозяйства нет вариантов плана на сезон {year} — считать нечего.
          </AlertDescription>
        </Alert>
      )}

      {data && (
        <>
          {data.calculation.is_stale && (
            <StalePanel
              summary={data}
              busy={recalculate.isPending}
              canWrite={canWrite}
              onRecalculate={() => recalculate.mutate({ scenarioId: data.scenario.id })}
            />
          )}

          {data.totals.rows_calculated === 0 ? (
            <Card>
              <CardContent className="p-4">
                <h2 className="text-sm font-semibold text-foreground">
                  Вариант «{data.scenario.name}» · сводка не считается
                </h2>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Структура посевов есть, но техкарты не назначены ни на одну культуру. Валовку
                  показать можно (она зависит от плановой урожайности), деньги — нет.
                </p>
                <Button
                  size="sm"
                  className="mt-3"
                  onClick={() =>
                    navigate({
                      to: '/planning/seasons/$year/plan-variant',
                      params: { year: String(year) },
                      search: { variant: variantId ?? undefined },
                    })
                  }
                >
                  Назначить техкарты →
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <BasisToggle basis={basis} onChange={setBasis} />
              <KpiCard summary={data} />
              {data.targets && <TargetsCard summary={data} year={year} />}
              <WipCard summary={data} />
              <CropsTable summary={data} />
              <CostSplitCard summary={data} />
              {/* ADR-071 D9-D10: календарь затрат и закупки ТМЦ всего варианта
                  — те же числа, что на картах, одним движком. */}
              <MonthlyReportBlock scope={{ kind: 'scenario', scenarioId: data.scenario.id }} />
              <ProcurementBlock scope={{ kind: 'scenario', scenarioId: data.scenario.id }} />
            </>
          )}

          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-baseline gap-2">
                <h2 className="text-sm font-semibold text-foreground">Против другого варианта</h2>
                <span className="ml-auto text-xs text-muted-foreground">быстрая сверка двух</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {otherVariants.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    У хозяйства только один вариант плана — сравнивать не с чем.
                  </p>
                )}
                {otherVariants.map((other) => (
                  <Button
                    key={other.id}
                    size="sm"
                    variant={compareId === other.id ? 'default' : 'outline'}
                    onClick={() => setCompareId(compareId === other.id ? null : other.id)}
                  >
                    {other.name}
                  </Button>
                ))}
              </div>
              {compareId && compare.isLoading && <Skeleton className="mt-3 h-40 w-full" />}
              {compare.data && (
                <Table className="mt-3">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Показатель</TableHead>
                      <TableHead className="text-right">{data.scenario.name}</TableHead>
                      <TableHead className="text-right">{compare.data.scenario.name}</TableHead>
                      <TableHead className="text-right">Разница</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {compareVariants(data.totals, compare.data.totals).map((line) => {
                      const good =
                        line.delta === null
                          ? null
                          : line.betterWhen === 'higher'
                            ? line.delta > 0
                            : line.delta < 0;
                      return (
                        <TableRow key={line.label}>
                          <TableCell className="text-muted-foreground">{line.label}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatValueByUnit(line.left, line.unit)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatValueByUnit(line.right, line.unit)}
                          </TableCell>
                          <TableCell
                            className={
                              good === null
                                ? 'text-right tabular-nums text-muted-foreground'
                                : good
                                  ? 'text-right font-medium tabular-nums text-primary'
                                  : 'text-right font-medium tabular-nums text-destructive'
                            }
                          >
                            {formatDeltaByUnit(line.delta, line.unit)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              {compare.data && compare.data.totals.not_in_calc.unassigned_ha > 0 && (
                <p className="mt-2 text-xs text-accent-brand">
                  Вариант «{compare.data.scenario.name}» посчитан частично:{' '}
                  {formatHa(compare.data.totals.not_in_calc.unassigned_ha)} без техкарт — сравнение
                  неполное.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Shell>
  );
}

function BasisToggle({ basis, onChange }: { basis: SummaryBasis; onChange: (b: SummaryBasis) => void }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold text-foreground">Подача затрат</h2>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant={basis === 'harvest' ? 'default' : 'outline'} onClick={() => onChange('harvest')}>
              По урожаю
            </Button>
            <Button size="sm" variant={basis === 'cash' ? 'default' : 'outline'} onClick={() => onChange('cash')}>
              По календарю
            </Button>
          </div>
        </div>
        <p className="mt-2 text-xs leading-5 text-muted-foreground">
          {basis === 'harvest'
            ? 'По урожаю — все затраты этого урожая, включая НЗП: осенние работы под него, выполненные в прошлом сезоне. Так считаются себестоимость и маржа.'
            : 'По календарю — деньги этого года: без осенних работ прошлого сезона, но с осенними работами под следующий урожай. Так считаются закупки и потребность в деньгах.'}
        </p>
      </CardContent>
    </Card>
  );
}

function WipCard({ summary }: { summary: VariantSummary }) {
  const wip = summary.totals.wip;
  const [showMonths, setShowMonths] = useState(false);
  const report = useVariantWipReport(summary.scenario.id, showMonths);
  // Без НЗП-денег карточка — шум (яровое хозяйство её не увидит вовсе).
  if (!wip || (wip.incoming === 0 && (wip.outgoing ?? 0) === 0)) return null;
  const nextYear = summary.scenario.season_year + 1;
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-baseline gap-2">
          <h2 className="text-sm font-semibold text-foreground">НЗП — незавершённое производство</h2>
          <span className="ml-auto text-xs text-muted-foreground">
            осенние работы под этот урожай, выполненные в прошлом сезоне
          </span>
        </div>
        <TileGrid cols={3} className="mt-3">
          <Kpi label={`Входящее НЗП (под урожай ${summary.scenario.season_year})`} value={formatMln(wip.incoming)} />
          <Kpi
            label={`Исходящее НЗП (под урожай ${wip.outgoing_source?.season_year ?? nextYear})`}
            value={wip.outgoing === null ? '—' : formatMln(wip.outgoing)}
          />
          {/* Это ДРУГОЕ число, чем «затраты» в итогах: там по урожаю − входящее,
              здесь ещё плюс осень под следующий урожай. Подпись обязана
              разводить их (should-fix архитектора). */}
          <Kpi label="Деньги календарного года (− входящее, + исходящее)" value={formatMln(wip.cash_in_season)} />
        </TileGrid>
        {wip.outgoing_status === 'next_season_not_planned' && (
          <p className="mt-3 text-xs leading-5 text-accent-brand">
            Исходящее НЗП появится, когда откроете план {nextYear} года: осенний сев под будущий
            урожай живёт в картах следующего сезона. Пока деньги календарного года посчитаны без него.
          </p>
        )}
        {wip.outgoing_status === 'next_season_draft' && wip.outgoing_source && (
          <p className="mt-3 text-xs leading-5 text-accent-brand">
            Исходящее НЗП посчитано по черновику «{wip.outgoing_source.name}» — он ещё не выбран
            планом сезона {wip.outgoing_source.season_year}, и с его правками деньги календарного
            года будут меняться.
          </p>
        )}
        {wip.outgoing_status === 'not_requested' && (
          <p className="mt-3 text-xs leading-5 text-muted-foreground">
            Исходящее НЗП в этом расчёте не запрашивалось — деньги календарного года посчитаны без
            него.
          </p>
        )}
        <div className="mt-3">
          <Button size="sm" variant="outline" onClick={() => setShowMonths((v) => !v)}>
            {showMonths ? 'Скрыть по месяцам' : 'Показать по месяцам'}
          </Button>
        </div>
        {showMonths && report.isLoading && <Skeleton className="mt-3 h-24 w-full" />}
        {showMonths && report.data && (
          <Table className="mt-3">
            <TableHeader>
              <TableRow>
                <TableHead>Месяц</TableHead>
                <TableHead>Куда отнесено</TableHead>
                <TableHead className="text-right">ТМЦ</TableHead>
                <TableHead className="text-right">ГСМ</TableHead>
                <TableHead className="text-right">Оплата труда</TableHead>
                <TableHead className="text-right">Наёмные работы</TableHead>
                <TableHead className="text-right">Итого</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[
                ...report.data.incoming.months.map((m) => ({ ...m, side: `урожай ${report.data!.season_year}` })),
                ...(report.data.outgoing?.months ?? []).map((m) => ({
                  ...m,
                  side: `урожай ${report.data!.next_season_year ?? nextYear}`,
                })),
              ].map((m, i) => (
                <TableRow key={`${m.side}-${m.month ?? 'none'}-${i}`}>
                  <TableCell>{formatWipMonth(m.month)}</TableCell>
                  <TableCell className="text-muted-foreground">{m.side}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatMln(m.materials)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatMln(m.fuel)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatMln(m.labor)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatMln(m.outsource)}</TableCell>
                  <TableCell className="text-right font-medium tabular-nums">{formatMln(m.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// Экспорт ради render-теста: карточка чисто презентационная (без react-query
// и AppShell), и подпись затрат по подаче — контракт, который легко потерять
// при следующей правке шапки (ревью head-web по #827).
export function KpiCard({ summary }: { summary: VariantSummary }) {
  const t = summary.totals;
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-baseline gap-2">
          <h2 className="text-sm font-semibold text-foreground">Итог варианта</h2>
          <span className="ml-auto text-xs text-muted-foreground">
            посчитано по бюджетным справочникам на сегодня
          </span>
        </div>
        {/* Подпись основы расчёта (требование head-backend на #817). Экран
            посевного плана считает валовку по урожайности, вписанной в строках
            полей, а здесь она берётся с назначенных техкарт — числа законно
            расходятся. Без подписи человек, сверивший их, решит, что один из
            экранов врёт, и перестанет верить обоим. */}
        <p className="mt-1 text-xs text-muted-foreground">
          Экономика — по назначенным техкартам: урожайность берётся с них. В посевном
          плане валовка считается по урожайности, вписанной в строках полей, поэтому
          цифры там могут отличаться.
        </p>
        {/* Подписи затрат разводим по подаче (should-fix архитектора): в cash
            рядом стоят ДВА разных числа — «по урожаю − входящее НЗП» здесь и
            «деньги календарного года» (с исходящим) в блоке НЗП. Без подписи
            человек, сверивший их, решит, что одно из них врёт. */}
        <TileGrid cols={3} className="mt-3">
          <Kpi label="Валовой сбор" value={formatTons(t.gross_t)} />
          <Kpi
            label={
              summary.basis === 'cash' ? 'Затраты по урожаю − входящее НЗП' : 'Прямые затраты'
            }
            value={formatMln(t.cost)}
          />
          <Kpi label="Затраты, ₽/га" value={formatRubPerHa(t.cost_per_ha)} />
          {t.prices_visible && <Kpi label="Выручка" value={formatMln(t.revenue)} />}
          {/* В подаче «по календарю» маржи нет по инварианту ADR-070 — плитки
              прячем целиком, а не рисуем ряд прочерков (ревью head-web):
              причина объяснена словами ниже. */}
          {t.prices_visible && t.margin_reason == null && (
            <>
              <Kpi label="Маржа" value={formatMln(t.margin)} />
              <Kpi label="Маржа с гектара" value={formatRubPerHa(t.margin_per_ha)} />
              <Kpi label="Маржа, %" value={formatPct(t.margin_pct)} />
            </>
          )}
        </TileGrid>
        {t.margin_reason != null && (
          <p className="mt-3 text-xs leading-5 text-accent-brand">
            Маржа считается только по урожаю: выручка урожая должна встречаться со всеми его
            затратами, включая осенние прошлого сезона. Переключитесь на подачу «По урожаю».
          </p>
        )}
        {!t.prices_visible && (
          <p className="mt-3 text-xs leading-5 text-accent-brand">
            Выручка и маржа не показаны: они считаются по бюджетным справочникам, а доступа к ним у вас
            нет. Валовка и себестоимость — полностью ваши.
          </p>
        )}
        {t.not_in_calc.incomplete_rows > 0 && (
          <p className="mt-3 text-xs leading-5 text-accent-brand">
            В маржу не вошли {formatHa(t.not_in_calc.incomplete_ha)} с назначенной техкартой:{' '}
            {t.not_in_calc.reasons.map((r) => `${formatHa(r.area_ha)} — ${r.reason}`).join('; ')}. Их
            затраты тоже не в итоге: складывать выручку одних строк с затратами других значит занижать
            маржу.
          </p>
        )}
        <p className="mt-3 text-xs leading-5 text-muted-foreground">
          Маржа с гектара считается на всю пашню {formatHa(summary.coverage.arable_ha)}, включая пар и
          нераспределённые поля — иначе показатель льстит хозяйству. Прямые затраты — то, что
          развёрнуто в техкартах: ТМЦ, ГСМ, оплата труда, наёмные работы и аутсорс. Общехозяйственные
          расходы в вариант не входят.
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * «Против цели сезона» — светофор варианта против цели СВОЕГО уровня
 * (ADR-069): вариант хозяйства сверяется с farm-целью, выручка сверху не
 * наследуется. Факт — те же totals, что в «Итоге варианта» (подача «по
 * урожаю», ADR-070 D3). При неполном покрытии сверка честно выключается:
 * недопокрытый вариант занижает выручку, и красный был бы враньём.
 */
function TargetsCard({ summary, year }: { summary: VariantSummary; year: number }) {
  const t = summary.targets;
  if (!t) return null;
  const metrics: { label: string; metric: TargetMetric; unit: 'money' | 'pct' }[] = [
    { label: 'Выручка', metric: t.revenue, unit: 'money' },
    { label: 'Прибыль', metric: t.margin, unit: 'money' },
    { label: 'Прибыль, %', metric: t.margin_pct, unit: 'pct' },
  ];
  const anySet = metrics.some((m) => m.metric.target !== null);
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-baseline gap-2">
          <h2 className="text-sm font-semibold text-foreground">Против цели сезона</h2>
          <Link
            className="ml-auto text-xs text-primary underline underline-offset-2"
            to="/planning/seasons/$year/budget-targets"
            params={{ year: String(year) }}
          >
            цели сезона →
          </Link>
        </div>
        {!t.comparable && (
          <p className="mt-2 text-xs leading-5 text-accent-brand">
            Сверка с целью невозможна: {formatHa(summary.coverage.unassigned_ha)} без техкарт —
            вариант посчитан не полностью, и судить его по цели было бы нечестно. Назначьте техкарты
            на все строки.
          </p>
        )}
        <TileGrid cols={3} className="mt-3">
          {metrics.map(({ label, metric, unit }) => (
            <div key={label} className="rounded-lg border border-border p-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{label}</span>
                <Badge variant={VERDICT_BADGE[metric.verdict]} className="ml-auto">
                  {VERDICT_TITLES[metric.verdict]}
                </Badge>
              </div>
              <div className="mt-1 text-sm tabular-nums text-foreground">
                {unit === 'money'
                  ? `${formatMln(metric.fact)} из ${formatMln(metric.target)}`
                  : `${formatPct(metric.fact)} из ${formatPct(metric.target)}`}
                {metric.ratio !== null && (
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({Math.round(metric.ratio * 100)} % цели)
                  </span>
                )}
              </div>
              {metric.inherited_from && metric.inherited_from !== 'farm' && (
                <div className="mt-1 text-xs text-muted-foreground">
                  цель уровня {metric.inherited_from === 'cluster' ? 'кластера' : 'холдинга'}
                </div>
              )}
            </div>
          ))}
        </TileGrid>
        {t.revenue.verdict === 'not_set' && t.higher_level_revenue && (
          <p className="mt-2 text-xs leading-5 text-muted-foreground">
            Цель по выручке на это хозяйство не задана — сверка идёт на своде кластера или холдинга.
          </p>
        )}
        {anySet && (
          <p className="mt-2 text-xs leading-5 text-muted-foreground">
            Недостижение цели — подсветка, а не запрет: утверждать вариант можно и при «ниже цели».
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}

function CropsTable({ summary }: { summary: VariantSummary }) {
  const groups = groupRowsByCrop(summary.rows, summary.coverage.arable_ha);
  const t = summary.totals;
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-baseline gap-2">
          <h2 className="text-sm font-semibold text-foreground">По культурам</h2>
          <span className="ml-auto text-xs text-muted-foreground">
            строки культуры раскрыты до «культура по предшественнику»
          </span>
        </div>
        <Table className="mt-3">
          <TableHeader>
            <TableRow>
              <TableHead>Культура</TableHead>
              <TableHead className="text-right">Площадь, га</TableHead>
              <TableHead className="text-right">Урожайность, ц/га</TableHead>
              <TableHead className="text-right">Валовка, т</TableHead>
              <TableHead className="text-right">Цена, ₽/т</TableHead>
              <TableHead className="text-right">Выручка</TableHead>
              <TableHead>Вариант техкарты</TableHead>
              <TableHead className="text-right">Затраты, ₽/га</TableHead>
              <TableHead className="text-right">Затраты</TableHead>
              <TableHead className="text-right">Маржа, ₽/га</TableHead>
              <TableHead className="text-right">Маржа, %</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.flatMap((group) => [
              <TableRow key={group.title} className="bg-muted/40 hover:bg-muted/40">
                <TableCell className="font-medium text-foreground">{group.title}</TableCell>
                <TableCell className="text-right tabular-nums">{Math.round(group.area_ha).toLocaleString('ru-RU')}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {group.yield_c_ha === null ? '—' : group.yield_c_ha.toFixed(1)}
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatTons(group.gross_t)}</TableCell>
                <TableCell className="text-right text-muted-foreground">—</TableCell>
                <TableCell className="text-right tabular-nums">{formatMln(group.revenue)}</TableCell>
                <TableCell className="text-muted-foreground">
                  {group.rows.length > 1 ? (
                    `${group.assigned} из ${group.rows.length} строк`
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      {group.rows[0]?.tech_card?.name ?? '—'}
                      {group.rows[0]?.is_wip && (
                        <Badge
                          variant="outline"
                          title="НЗП — осенние работы под этот урожай, выполненные в прошлом сезоне"
                        >
                          НЗП
                        </Badge>
                      )}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatRubPerHa(group.cost_per_ha)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatMln(group.cost_total)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatRubPerHa(group.margin_per_ha)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatPct(group.margin_pct)}</TableCell>
              </TableRow>,
              ...(group.rows.length > 1
                ? group.rows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell className="pl-6 text-muted-foreground">↳ {rowTitle(row)}</TableCell>
                      <TableCell className="text-right tabular-nums">{Math.round(row.area_ha).toLocaleString('ru-RU')}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.yield_c_ha === null ? '—' : row.yield_c_ha.toFixed(1)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatTons(row.gross_t)}</TableCell>
                      {/* «Цена, ₽/т» — цена, а не агрегат расчёта: два знака
                          обязательны (AK-704). Соседние ₽-колонки — суммы по
                          площади, они честно живут в млн и копеек не просят. */}
                      <TableCell className="text-right tabular-nums">
                        {row.price_per_t === null ? '—' : formatMoneyNumber(row.price_per_t)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatMln(row.revenue)}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-2">
                          {row.tech_card?.name ?? <span className="text-accent-brand">без техкарты</span>}
                          {row.is_wip && (
                            <Badge
                              variant="outline"
                              title="НЗП — осенние работы под этот урожай, выполненные в прошлом сезоне"
                            >
                              НЗП
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatRubPerHa(row.cost_per_ha)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatMln(row.cost_total)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatRubPerHa(row.margin_per_ha)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatPct(row.margin_pct)}</TableCell>
                    </TableRow>
                  ))
                : []),
            ])}

            <TableRow className="bg-muted/60 hover:bg-muted/60 font-semibold">
              <TableCell>Итого по варианту</TableCell>
              <TableCell className="text-right tabular-nums">{Math.round(t.area_ha).toLocaleString('ru-RU')}</TableCell>
              <TableCell className="text-right text-muted-foreground">—</TableCell>
              <TableCell className="text-right tabular-nums">{formatTons(t.gross_t)}</TableCell>
              <TableCell className="text-right text-muted-foreground">—</TableCell>
              <TableCell className="text-right tabular-nums">{formatMln(t.revenue)}</TableCell>
              <TableCell className="text-muted-foreground">{t.rows_calculated} строк в расчёте</TableCell>
              <TableCell className="text-right tabular-nums">{formatRubPerHa(t.cost_per_ha)}</TableCell>
              <TableCell className="text-right tabular-nums">{formatMln(t.cost)}</TableCell>
              <TableCell className="text-right tabular-nums">{formatRubPerHa(t.margin_per_ha)}</TableCell>
              <TableCell className="text-right tabular-nums">{formatPct(t.margin_pct)}</TableCell>
            </TableRow>

            {t.not_in_calc.area_ha > 0 && (
              <TableRow className="bg-accent-brand/5 hover:bg-accent-brand/5">
                <TableCell className="text-accent-brand">
                  Не в расчёте: {formatHa(t.not_in_calc.unassigned_ha)} без техкарты,{' '}
                  {formatHa(t.not_in_calc.no_crop_ha)} без культуры, {formatHa(t.not_in_calc.fallow_ha)} пар
                </TableCell>
                <TableCell className="text-right tabular-nums text-accent-brand">
                  {Math.round(t.not_in_calc.area_ha).toLocaleString('ru-RU')}
                </TableCell>
                <TableCell colSpan={9} className="text-muted-foreground">
                  эти гектары не считаются нулём и не размазываются по остальным культурам
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function CostSplitCard({ summary }: { summary: VariantSummary }) {
  const split = costSplit(summary.rows);
  if (split.length === 0) return null;
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-baseline gap-2">
          <h2 className="text-sm font-semibold text-foreground">Из чего сложились затраты</h2>
          <span className="ml-auto text-xs text-muted-foreground">{formatMln(summary.totals.cost)}</span>
        </div>
        <div className="mt-3 flex h-3 overflow-hidden rounded-full border border-border">
          {split.map((part, i) => (
            <span
              key={part.kind}
              className={['bg-primary', 'bg-primary/70', 'bg-primary/45', 'bg-primary/25'][i] ?? 'bg-muted'}
              style={{ width: `${part.pct}%` }}
            />
          ))}
        </div>
        <dl className="mt-3">
          {split.map((part) => (
            <div key={part.kind} className="flex min-h-10 items-center border-b border-border py-2 text-sm leading-6">
              <dt className="text-muted-foreground">{part.title}</dt>
              <dd className="ml-auto tabular-nums text-foreground">
                {formatPct(part.pct)} · {formatMln(part.amount)}
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-2 text-xs leading-5 text-muted-foreground">
          Отсюда же вырастет список закупок сезона — та же потребность, только в натуре.
        </p>
      </CardContent>
    </Card>
  );
}

function StalePanel({
  summary,
  busy,
  canWrite,
  onRecalculate,
}: {
  summary: VariantSummary;
  busy: boolean;
  canWrite: boolean;
  onRecalculate: () => void;
}) {
  const calc = summary.calculation;
  return (
    <Card className="border-accent-brand/40 bg-accent-brand/5">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold text-accent-brand">
            База расчёта ушла вперёд — после расчёта менялись цены и техкарты
          </h2>
          <Badge variant="accent-brand">посчитано {formatDate(calc.calculated_at)}</Badge>
        </div>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          Система не пересчитывает молча: сначала показываем, что именно изменилось, и только потом
          пересчитываем по кнопке.
        </p>
        {calc.changes.length > 0 && (
          <Table className="mt-3">
            <TableHeader>
              <TableRow>
                <TableHead>Что изменилось</TableHead>
                <TableHead>Когда</TableHead>
                <TableHead>Подробности</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {calc.changes.slice(0, 12).map((change, i) => (
                <TableRow key={`${change.kind}-${change.changed_at}-${i}`}>
                  <TableCell className="text-foreground">
                    {change.kind === 'catalog' ? (
                      <>
                        Бюджетный справочник «{change.catalog_title}»
                        <div className="text-xs text-muted-foreground">{change.what}</div>
                      </>
                    ) : (
                      change.what
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(change.changed_at)}</TableCell>
                  <TableCell className="text-muted-foreground">{changeDetails(change)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={!canWrite || busy} onClick={onRecalculate}>
            Пересчитать вариант
          </Button>
          <span className="text-xs text-muted-foreground">
            Числа выше уже посчитаны по текущим справочникам; кнопка переносит базу расчёта на них,
            чтобы «устарело» отсчитывалось заново.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function changeDetails(change: CalcChange): string {
  if (change.kind === 'tech_card') {
    return change.area_ha ? `вариант правился, он стоит на ${formatHa(change.area_ha)}` : 'вариант правился';
  }
  const field = change.field_name ? `${change.field_name}: ` : '';
  if (change.old_value && change.new_value) return `${field}${change.old_value} → ${change.new_value}`;
  if (change.new_value) return `${field}${change.new_value}`;
  return field || 'правка справочника';
}

function Shell({ year, title, children }: { year?: string; title?: string; children: React.ReactNode }) {
  return (
    // AK-866 В-3: область — из переключателя. Своего выбора хозяйства на
    // экране нет; `POST /scope/set` отсюда зовётся только deep-link'ом
    // `?farm=` и только через `useScopeSetter` (AK-897).
    <AppShell>
      <PageHeader
        title={title ?? 'Сводка варианта посевного плана'}
        description="Что вариант даёт в тоннах и деньгах: валовка, выручка, затраты, маржа — по каждой культуре и в целом. Считается из назначенных техкарт и бюджетных справочников сезона."
        breadcrumbs={[
          { label: 'Планирование сезона', to: '/planning' },
          // Средняя крошка ведёт в хаб бюджета сезона (макет v2-00).
          {
            label: `Бюджет сезона ${year ?? ''}`.trim(),
            ...(year ? { to: `/planning/seasons/${year}/budget` } : {}),
          },
          { label: 'Сводка варианта' },
        ]}
      />
      <div className="mt-4 flex flex-col gap-4">{children}</div>
    </AppShell>
  );
}
